/**
 * EXPOSURE（03 露出）
 *
 * - 基準露出：絞りとシャッターを最大の文字で。EV の行、L-858D 入力時はその換算の行。ほかに最大 5 組
 * - ゾーン分布：各ピンが「置いたゾーン」から「落ちるゾーン」へ動く。目標は点線
 * - ダイナミックレンジと現像方針（黒い面が起き上がる）、理由の 1 文
 * - テンプレート使用時は「推奨 / 計算」を並べて比較
 * - 主要シャドウがないときは案内と「測光に戻る」
 * - 下端「NEXT 04 現像へ進む」、副ボタン「あとで現像する（保存して閉じる）」
 *
 * 計算は zone-calc.js（要件定義書 §4）。結果は scene.js がピンの変更時に自動で書き戻す。
 */

import { showScreen, resetScreen, escapeHtml, toast, zoneToRoman, icon, renderRail, zoneCellsHtml, chapterLink, buzz, confirmDialog } from './ui.js';
import { getCurrentShoot, updateCurrentShoot, flushSave, committedSpots } from './scene.js';
import { FILTER_FACTORS } from './presets.js';
import { computeShootResult, applyEiToEV, planLabel, rangeOf } from './zone-calc.js';
import { prefs } from './prefs.js';
import { isoIndexOf, apertureIndexOf, shutterForEv, isoLabel, APL } from './sekonic.js';
import { templateOf } from './scene-templates.js';

export * from './zone-calc.js';

export function openExposure(opts = {}) {
  const shoot = getCurrentShoot();
  if (!shoot) { toast('シーンがありません'); showScreen('home', { back: true }); return; }
  render();
  showScreen('exposure', { back: !!opts.back });
}

const fmt2 = (x) => String(Math.round(x * 100) / 100);
/** 目標との差（段）。diffStops が＋なら目標より露光が少ない */
const diffText = (d) => (Math.abs(d) < 0.05 ? 'ほぼ一致' : `露光 ${Math.abs(d).toFixed(2)} 段${d > 0 ? '少なめ' : '多め'}`);

function render() {
  const el = document.getElementById('screen-exposure');
  const shoot = getCurrentShoot();
  const spots = committedSpots(shoot);
  const r = computeShootResult({ ...shoot, spots });
  const ok = r && r.shootEV != null;

  let main = '';
  if (!ok) {
    main = `
      <div style="border-top:1px solid var(--color-ink);margin-top:10px;padding:18px 0 4px;animation:zn-up .5s both">
        <div style="font:500 20px/1.4 var(--font-jp-serif)">主要シャドウを設定</div>
        <p class="sub" style="margin:6px 0 0;font-size:13px;line-height:1.7">スポットの役割を「主要シャドウ」にしてください。露出はこのピンから計算します。</p>
        <button class="btn-line" type="button" data-to-map style="margin-top:14px">測光に戻る</button>
      </div>`;
  } else {
    const evEi = applyEiToEV(r.shootEV, shoot.ei);
    const ap = r.primary ? r.primary.aperture : '—';
    const sh = r.primary ? r.primary.shutter.replace(/s$/, '') : '—';
    const others = r.combos.filter((c) => !c.primary).slice(0, 5);
    let sekLine = '';
    const usesSek = prefs.meterInput === 'sek' || spots.some((s) => s.reading?.mode === 'sek');
    if (usesSek) {
      const isoK = isoIndexOf(shoot.meterIso ?? shoot.ei);
      const lastRd = [...spots].reverse().find((s) => s.reading?.mode === 'sek')?.reading;
      const apI = apertureIndexOf(lastRd?.aperture ?? prefs.sekAperture);
      sekLine = `L-858D 換算（ISO ${isoLabel(isoK)} · f/${APL[apI]}）：${shutterForEv(r.shootEV + isoK / 3, { ap: apI, isoK: 0 })}`;
    }
    main = `
      <div class="expo-pair">
        <div><div class="field__label" style="margin:0">絞り</div><div class="big-num">${escapeHtml(ap)}</div></div>
        <div><div class="field__label" style="margin:0">シャッター</div><div class="big-num" style="animation-delay:.22s">${escapeHtml(sh)}${r.primary ? '<small>s</small>' : ''}</div></div>
      </div>
      <div class="en sub" style="font-size:12px;line-height:1.5;margin-top:10px;animation:zn-fade .5s .35s both">EV ${fmt2(r.shootEV)} (ISO 100) · 実効 EV ${fmt2(evEi)} @ EI ${escapeHtml(String(shoot.ei))}${r.primary ? ` · 目標との差 ${diffText(r.primary.diffStops)}` : ''}</div>
      ${r.primary ? '' : outOfRange(shoot, evEi - r.filterStops)}
      ${filterRow(shoot, r)}
      ${sekLine ? `<div style="font:500 12px/1.5 var(--font-jp-sans);margin-top:4px;animation:zn-fade .5s .45s both">${escapeHtml(sekLine)}</div>` : ''}
      <div class="combos">
        ${others.map((c, i) => `<button type="button" data-shot="${r.combos.indexOf(c)}" style="animation-delay:${500 + i * 60}ms" aria-label="${escapeHtml(`${c.aperture} ${c.shutter} で撮ったと記録`)}">${escapeHtml(`${c.aperture} · ${c.shutter.replace(/s$/, '')}`)}${Math.abs(c.diffStops) >= 0.05 ? `<small class="sub" style="font-size:10px;margin-left:4px">${escapeHtml(`${Math.abs(c.diffStops).toFixed(2)} 段${c.diffStops > 0 ? '少なめ' : '多め'}`)}</small>` : ''}</button>`).join('')}
        ${others.length === 0 && r.primary ? '<span class="sub" style="box-shadow:none;padding:0;font:400 11px/1.5 var(--font-jp-sans)">ほかに範囲内の組合せはありません</span>' : ''}
      </div>
      ${shotCard(shoot, r)}`;
  }

  let lower = '';
  if (ok) {
    const pos = (z) => `${Math.max(2.5, Math.min(97.5, ((z + 0.5) / 11) * 100))}%`;
    const groups = {};
    const marks = r.spotsWithFallen.map((s, i) => {
      const f = Math.max(0, Math.min(10, s.fallenZone));
      const key = Math.round(f);
      groups[key] = groups[key] === undefined ? 0 : groups[key] + 1;
      const anchor = s.role === 'shadow-anchor' || s.role === 'highlight-anchor';
      return `<div class="dist__m" data-from="${pos(s.placedZone)}" data-to="${pos(f)}" style="left:${pos(s.placedZone)};transition-delay:${i * 110}ms">
        <div class="dist__b ${anchor ? 'is-anchor' : ''}" style="top:${-groups[key] * 28}px;animation-delay:${i * 110}ms">${i + 1}</div><span class="dist__l"></span></div>`;
    }).join('');
    let dr = '<div style="font:300 40px/1 var(--font-en);margin-top:10px">—</div><div class="sub" style="font-size:11px;line-height:1.5;margin-top:8px">主要ハイライトを設定すると表示</div>';
    let plan = '—';
    let planShort = '未設定';
    let reason = '主要ハイライトのピンを置くと、現像方針を計算します。';
    if (r.highlight) {
      const hf = Math.round(Math.max(0, Math.min(10, r.spotsWithFallen.find((s) => s.id === r.highlight.id)?.fallenZone ?? r.highlight.placedZone)));
      const d = r.planDiff;
      const hz = zoneToRoman(r.highlight.placedZone);
      dr = `<div style="font:300 40px/1 var(--font-en);margin-top:10px">${fmt2(r.dynamicRange)}<span style="font:400 14px/1 var(--font-jp-sans);margin-left:4px">段</span></div>
        <div class="sub" style="font-size:11px;line-height:1.5;margin-top:8px">シャドウ ${zoneToRoman(r.shadow.placedZone)} → ハイライト ${hz}（実際は ${zoneToRoman(hf)}）</div>`;
      plan = planLabel(r.plan);
      planShort = d === 0 ? '標準現像' : d > 0 ? `${d} 段の圧縮現像` : `${-d} 段の拡張現像`;
      reason = d === 0
        ? `ハイライトが目標ゾーン ${hz} に落ちるため、標準現像（N）で仕上げます。`
        : d > 0
          ? `ハイライトがゾーン ${zoneToRoman(hf)} に落ちるため、${d} 段の圧縮現像で目標ゾーン ${hz} に抑えます。`
          : `ハイライトがゾーン ${zoneToRoman(hf)} にとどまるため、${-d} 段の拡張現像で目標ゾーン ${hz} まで持ち上げます。`;
    }
    const tpl = templateOf(shoot);
    let tplHtml = '';
    if (tpl) {
      const same = planLabel(tpl.suggestedDevPlan) === plan;
      const note = plan === '—' ? '主要ハイライトを置くと比較できます' : same ? '計算結果と一致しています' : '計算結果を優先します。現像画面で変えられます';
      tplHtml = `
        <div class="tpl-cmp">
          <div style="min-width:0"><div class="field__label" style="margin:0;font-size:11px">テンプレート「${escapeHtml(tpl.name)}」の推奨</div><div style="font:400 12px/1.5 var(--font-jp-sans);margin-top:5px">${escapeHtml(note)}</div></div>
          <div style="display:flex;flex-direction:column;align-items:center;gap:4px"><span class="sub" style="font:500 10px/1 var(--font-jp-sans)">推奨</span><span class="en" style="font:400 22px/1 var(--font-en)">${escapeHtml(planLabel(tpl.suggestedDevPlan))}</span></div>
          <div style="display:flex;flex-direction:column;align-items:center;gap:4px;padding-left:12px;border-left:1px solid var(--color-rule)"><span class="sub" style="font:500 10px/1 var(--font-jp-sans)">計算</span><span class="en" style="font:500 22px/1 var(--font-en)">${escapeHtml(plan)}</span></div>
        </div>`;
    }
    lower = `
      <div style="padding:22px var(--gutter) 0">
        <div style="display:flex;justify-content:space-between" class="eyebrow"><span>distribution</span><span class="sec-head__jp">置いたゾーン → 落ちるゾーン</span></div>
        <div class="dist" id="exp-dist">
          ${r.highlight ? `<div class="dist__m" style="left:${pos(r.highlight.placedZone)};opacity:0;transition:opacity .5s .5s" id="exp-ghost"><span class="dist__ghost">目標</span></div>` : ''}
          ${marks}
        </div>
        <div class="zstrip" style="height:30px;box-shadow:inset 0 0 0 1px var(--color-rule)">${zoneCellsHtml()}</div>
        <div class="zlabels" style="margin-top:7px;color:var(--color-muted-ink)">${['0', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'].map((t) => `<span>${t}</span>`).join('')}</div>
      </div>
      <div class="dr-plan">
        <div style="padding:12px 14px 0 0"><div class="field__label" style="margin:0;font-size:11px">ダイナミックレンジ</div>${dr}</div>
        <div class="plan-card"><div style="font:500 11px/1 var(--font-jp-sans);opacity:.75">現像方針</div><div style="font:300 46px/1 var(--font-en);margin-top:8px;letter-spacing:-.02em">${escapeHtml(plan)}</div><div style="font:400 11px/1.5 var(--font-jp-sans);margin-top:8px;opacity:.85">${escapeHtml(planShort)}</div></div>
      </div>
      <p style="margin:12px var(--gutter) 0;font:400 13px/1.75 var(--font-jp-sans);animation:zn-fade .6s 1.6s both">${escapeHtml(reason)}</p>
      ${tplHtml}
      <div class="chapter-links">
        ${chapterLink('exposure', '04 露出設定')}
        ${chapterLink('development', '06 現像（N±）')}
        ${r.primary && /^(\d+)s$/.test(r.primary.shutter) ? chapterLink('reciprocity', '12 相反則不軌') : ''}
      </div>
      <div style="margin:12px var(--gutter) 0"><button class="btn-rule" type="button" data-ask-scene style="width:100%">${icon('chat', 16)}このシーンを AI に相談</button></div>`;
  }

  el.innerHTML = `
    <div class="safe-top"></div>
    <div class="bar">
      <button class="bar__back" type="button" data-to-map>${icon('back', 22)}測光</button>
      <div class="bar__title" style="margin-right:76px">露出</div>
    </div>
    <div id="exp-rail"></div>
    <div class="body" style="padding-bottom:16px">
      <div style="padding:14px var(--gutter) 0">
        <div style="display:flex;justify-content:space-between" class="eyebrow"><span>exposure</span><span class="sec-head__jp">基準露出</span></div>
        ${main}
      </div>
      ${lower}
    </div>
    <div class="foot" style="padding-bottom:calc(var(--safe-bottom) + 14px)">
      <button class="btn-primary btn-primary--next" type="button" data-to-dev>
        <span class="btn-primary__en">NEXT 04</span><span class="btn-primary__label">現像へ進む</span>${icon('moon', 16)}
      </button>
      <button class="btn-text" type="button" data-later style="margin-top:4px">あとで現像する（保存して閉じる）</button>
    </div>
  `;
  renderRail(el.querySelector('#exp-rail'), 2, { 1: () => backToMapping() });
  el.querySelectorAll('[data-to-map]').forEach((b) => b.addEventListener('click', backToMapping));
  el.querySelector('[data-ask-scene]')?.addEventListener('click', () => {
    import('./ask.js').then((m) => m.openAsk({ scene: getCurrentShoot() }));
  });
  // 3. 実際に切った露出を確定して記録
  const rerender = () => { const keep = el.querySelector('.body').scrollTop; render(); el.querySelector('.body').scrollTop = keep; };
  el.querySelectorAll('[data-shot]').forEach((b) => b.addEventListener('click', () => {
    const r = computeShootResult({ ...getCurrentShoot(), spots: committedSpots(getCurrentShoot()) });
    const c = r.combos[Number(b.dataset.shot)];
    if (!c) return;
    recordShot(c, r);
    buzz(10);
    toast(`${c.aperture} · ${c.shutter.replace(/s$/, '')} で撮ったと記録しました`);
    rerender();
  }));
  el.querySelectorAll('[data-bracket]').forEach((b) => b.addEventListener('click', () => {
    const cur = getCurrentShoot().shotExposure;
    if (!cur) return;
    updateCurrentShoot({ shotExposure: { ...cur, bracketStops: Number(b.dataset.bracket) } });
    buzz(6);
    rerender();
  }));
  el.querySelector('[data-shot-clear]')?.addEventListener('click', async () => {
    const ok = await confirmDialog({ title: '撮影した露出の記録を消しますか？', body: '推奨の露出の計算はそのまま残ります。', ok: '消す' });
    if (!ok) return;
    updateCurrentShoot({ shotExposure: null });
    rerender();
  });
  el.querySelector('[data-filter-comp]')?.addEventListener('click', () => {
    updateCurrentShoot({ filterComp: !getCurrentShoot().filterComp });
    const keep = el.querySelector('.body').scrollTop;
    render();
    el.querySelector('.body').scrollTop = keep;
  });
  el.querySelector('[data-to-dev]').addEventListener('click', () => import('./developer.js').then((m) => m.openDevelopment()));
  el.querySelector('[data-later]').addEventListener('click', async () => {
    try { await flushSave(); toast('保存しました。続きはホームの「続きから」へ'); } catch { toast('保存に失敗しました'); return; }
    resetScreen('home');
  });

  // ピンを「置いたゾーン」から「落ちるゾーン」へ動かす
  setTimeout(() => {
    el.querySelectorAll('#exp-dist [data-to]').forEach((m) => { m.style.left = m.dataset.to; });
    const g = el.querySelector('#exp-ghost');
    if (g) g.style.opacity = '1';
  }, 700);
}

/**
 * 3. 撮影した露出（Shoot.shotExposure）。確定した時点の条件ごと残し、後で計算し直さない。
 * { aperture, shutter, evSet, ei, filterStops, plan, bracketStops, at }
 */
function recordShot(c, r) {
  const shoot = getCurrentShoot();
  updateCurrentShoot({
    shotExposure: {
      aperture: c.aperture,
      shutter: c.shutter,
      evSet: Math.round(c.evCheck * 100) / 100,
      ei: shoot.ei,
      filterStops: r.filterStops || 0,
      plan: r.highlight ? r.plan : null,
      bracketStops: shoot.shotExposure?.bracketStops || 0,
      at: Date.now(),
    },
  });
}

function shotCard(shoot, r) {
  const x = shoot.shotExposure;
  const hint = '組合せをタップすると「撮影した露出」として記録します。後でピンや条件を変えても、この記録は変わりません。';
  if (!x) {
    return `<div style="margin-top:12px">${r.primary ? `<button type="button" class="btn-line" data-shot="${r.combos.indexOf(r.primary)}" style="height:44px">${icon('check', 16)}${escapeHtml(`${r.primary.aperture} · ${r.primary.shutter.replace(/s$/, '')}`)} で撮った</button>` : ''}<p class="sub" style="font-size:11.5px;line-height:1.6;margin:8px 0 0">${hint}</p></div>`;
  }
  const d = new Date(x.at);
  const brk = [[0, 'なし'], [0.333, '±⅓'], [0.5, '±½'], [1, '±1'], [2, '±2']];
  const differs = r.primary && (r.primary.aperture !== x.aperture || r.primary.shutter !== x.shutter);
  return `
    <div style="margin-top:14px;box-shadow:inset 0 0 0 1.5px var(--color-ink);padding:12px 14px">
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:8px">
        <span class="field__label" style="margin:0">撮影した露出（確定）</span>
        <span class="en sub" style="font-size:11px">${d.getHours()}:${String(d.getMinutes()).padStart(2, '0')} 記録</span>
      </div>
      <div style="font:400 26px/1.2 var(--font-en);margin-top:6px">${escapeHtml(`${x.aperture} · ${x.shutter.replace(/s$/, '')}`)}<span class="sub" style="font-size:13px;margin-left:8px">EI ${escapeHtml(String(x.ei))}${x.filterStops ? ` · フィルター込み` : ''}</span></div>
      ${differs ? `<div class="sub" style="font-size:11.5px;line-height:1.5;margin-top:4px">いまの推奨は ${escapeHtml(`${r.primary.aperture} · ${r.primary.shutter.replace(/s$/, '')}`)} です（条件を変えたため）。記録は撮影時のまま残します。</div>` : ''}
      <div class="field__label" style="margin:12px 0 6px">ブラケット</div>
      <div class="seg seg--md">${brk.map(([v, t]) => `<button type="button" class="seg__opt en ${Math.abs((x.bracketStops || 0) - v) < 0.01 ? 'is-on' : ''}" data-bracket="${v}">${t}</button>`).join('')}</div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px"><span class="sub" style="font-size:11.5px">ほかの組合せをタップすると記録を置き換えます</span><button type="button" data-shot-clear class="sub" style="font:500 12px/1 var(--font-jp-sans);text-decoration:underline;text-underline-offset:3px;padding:6px 0">記録を消す</button></div>
    </div>`;
}

/** 範囲に合う組合せがないときの案内（4. カメラの範囲を反映） */
function outOfRange(shoot, evSet) {
  const R = rangeOf(shoot);
  const fmtT = (t) => (t < 1 ? `1/${Math.round(1 / t)}` : `${t} 秒`);
  // 範囲のいちばん明るい側（開放・最遅）でも足りなければ暗すぎる
  const darkest = Math.log2((R.widest * R.widest) / R.slowest);
  const reason = evSet < darkest
    ? `暗すぎます。${fmtT(R.slowest)}を超える長時間露光（バルブ）になります。相反則不軌の補正も必要です`
    : '明るすぎます。ND フィルターや低感度のフィルムを検討してください';
  return `<div style="font:500 12px/1.6 var(--font-jp-sans);margin-top:6px">このカメラの範囲（f/${R.widest}〜${R.smallest}・${fmtT(R.fastest)}〜${fmtT(R.slowest)}）に合う組合せがありません。${reason}。</div>`;
}

/** 2. フィルター倍数を露出に加える切替（フィルターを付けているシーンだけ） */
function filterRow(shoot, r) {
  if (!shoot.filterId) return '';
  const f = FILTER_FACTORS[shoot.filterId];
  if (!f) return '';
  const on = !!shoot.filterComp;
  return `
    <div class="set-row set-row--flex" style="padding:12px 0;border-bottom:0;border-top:1px solid var(--color-rule);margin-top:10px">
      <div style="flex:1;min-width:0">
        <div style="font:500 14px/1.3 var(--font-jp-sans)">フィルター倍数を露出に加える</div>
        <div class="set-row__d">${escapeHtml(f.name)}・+${f.stops} 段。${on ? `加算済み（露出 EV ${fmt2(applyEiToEV(r.shootEV, shoot.ei) - r.filterStops)}）。` : ''}スポットメーターをフィルターなしで当てたときはオン、レンズ越しに測ったときはオフ。</div>
      </div>
      <button type="button" class="toggle ${on ? 'is-on' : ''}" data-filter-comp role="switch" aria-checked="${on}" aria-label="フィルター倍数を露出に加える"></button>
    </div>`;
}

function backToMapping() {
  import('./mapping.js').then((m) => m.openMapping({ back: true }));
}
